import { defaultParseSearch } from '@tanstack/react-router'
import { z } from 'zod'
import {
  type LogisticsSearch,
  logisticsSearchSchema,
  logisticsSearchWith,
  operationsTourSearch,
  type OperationsTourSearch,
  type SimulationDetailSearch,
  simulationDetailSearch,
} from './-search-schema'

/**
 * Écran d'où l'on a ouvert une fiche chauffeur ou camion (LU-181), porté par le paramètre `returnTo`
 * de la fiche : son retour y ramène, même tournée, même onglet, mêmes filtres. `from` est déjà pris
 * par le début de la période des filtres Logistique.
 */
export type RecordOrigin =
  | { kind: 'simulation'; id: number; search: SimulationDetailSearch }
  | { kind: 'operations'; id: number; search: OperationsTourSearch }
  | { kind: 'hub'; id: number; search: LogisticsSearch }

/**
 * Liste blanche des seuls écrans qui ouvrent ces fiches (liens de LU-175, personnel d'un hub), et
 * jamais un motif générique : un chemin interne, sans `//`, schéma ni hôte, ne peut pas mener hors du
 * BO. Leur recherche est revalidée par le schéma de leur route.
 */
const RECORD_ORIGINS = new Map<
  string,
  (id: number, search: Record<string, unknown>) => RecordOrigin
>([
  [
    'simulations',
    (id, search) => ({ kind: 'simulation', id, search: simulationDetailSearch(search) }),
  ],
  [
    'operations',
    (id, search) => ({ kind: 'operations', id, search: operationsTourSearch(search) }),
  ],
  ['hubs', (id, search) => ({ kind: 'hub', id, search: logisticsSearchSchema.parse(search) })],
])

const RECORD_ORIGIN_PATTERN = new RegExp(
  String.raw`^/logistics/(${[...RECORD_ORIGINS.keys()].join('|')})/([1-9]\d{0,9})(\?[^#\s\\]*)?$`,
)

/** Une origine plus longue n'a pas été écrite par le BO (filtres bornés à quelques centaines). */
const RECORD_ORIGIN_MAX_LENGTH = 2000

/** Origine `returnTo` d'une fiche, `undefined` si elle est absente ou hors de la liste blanche. */
export const recordOriginOf = (returnTo: unknown): RecordOrigin | undefined => {
  if (typeof returnTo !== 'string' || returnTo.length > RECORD_ORIGIN_MAX_LENGTH) return undefined
  const match = RECORD_ORIGIN_PATTERN.exec(returnTo)
  if (match === null) return undefined
  const [, segment, id, search = ''] = match
  return RECORD_ORIGINS.get(segment)?.(Number(id), defaultParseSearch(search))
}

/** `returnTo` accepté tel quel s'il est dans la liste blanche ; ignoré sinon (retour vers la liste). */
export const recordOriginSchema = z
  .string()
  .refine((returnTo) => recordOriginOf(returnTo) !== undefined)
  .optional()
  .catch(undefined)

/**
 * Recherche d'une fiche chauffeur ou camion : celle de la page Logistique (les filtres de la liste
 * suivent l'aller-retour) et son origine.
 */
export const logisticsRecordSearch = logisticsSearchWith({ returnTo: recordOriginSchema })

/**
 * Recherche des liens vers une fiche ouverts depuis l'écran `here` (chemin et recherche) : les
 * filtres de la page Logistique, et `here` comme origine s'il est dans la liste blanche. L'origine
 * est validée à la création de la fonction, pas à chaque rendu du lien qui l'appelle. Même résultat
 * que `logisticsRecordSearch` (vérifié par les tests).
 */
export const recordLinkSearch = (here: string) => {
  const returnTo = recordOriginSchema.parse(here)
  return (search: Record<string, unknown>): ReturnType<typeof logisticsRecordSearch> => ({
    ...logisticsSearchSchema.parse(search),
    returnTo,
  })
}
